import React from "react";

import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import { Link } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";

import { RoleName } from "../../../common/enums/RoleName";
import RoleBasedAccess from "../../../common/RoleBasedAccess";
import { Audiogram, AudiogramTemplate } from "../../types";
import AudiogramRenderer, {
  audiogramToEditorState,
} from "../templates/AudiogramRenderer";

interface Props {
  audiogram: Audiogram;
  episodeId: string;
  /** Denser variant used in the Episode preview (3-up grid, smaller type). */
  compact?: boolean;
}

const Card = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "12px",
  border: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  cursor: "pointer",
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "none",

  "&:hover": {
    borderColor: "#2563eb",
    boxShadow: "0 4px 16px rgba(37,99,235,0.10)",
  },
  // Reveal the dark scrim (via its background, so the button on top keeps a
  // full, opaque appearance) and show the Edit button on hover.
  "&:hover .audiogram-card__scrim": {
    backgroundColor: "rgba(15,23,42,0.82)",
    backdropFilter: "blur(4px)",
  },
  "&:hover .audiogram-card__action": {
    visibility: "visible",
  },

  [theme.breakpoints.down("sm")]: {
    "&:hover": {
      borderColor: theme.palette.divider,
      boxShadow: "none",
    },
    // Touch devices: no hover overlay; tapping the card opens the editor.
    "&:hover .audiogram-card__scrim": {
      backgroundColor: "transparent",
      backdropFilter: "none",
    },
    "&:hover .audiogram-card__action": {
      visibility: "hidden",
    },
  },

  "&.generating": {
    cursor: "not-allowed",
    opacity: 0.7,
    "&:hover": {
      borderColor: theme.palette.divider,
      boxShadow: "none",
    },
  },
}));

// Uniform square thumbnail area so every card in the grid has the same
// height regardless of the audiogram's own aspect ratio.
const PreviewContainer = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  aspectRatio: "1/1",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

// Width of the rendered audiogram inside the square thumbnail. The renderer
// derives its height from its width via CSS aspect-ratio, so sizing the width
// is enough: 9:16 must be narrower than the square (56.25%) to fit its full
// height, while 1:1 and 16:9 can span the full width.
const RENDER_WIDTHS: Record<Audiogram["aspect_ratio"], string> = {
  "9:16": "56.25%",
  "1:1": "100%",
  "16:9": "100%",
};

// Compact preview is a shorter 4:3 box (height = 75% of width) so the cards
// take less vertical space. Widths are recomputed so each audiogram is fitted
// by that reduced height rather than by width.
const COMPACT_PREVIEW_RATIO = "4 / 3";
const COMPACT_RENDER_WIDTHS: Record<Audiogram["aspect_ratio"], string> = {
  "9:16": "42%",
  "1:1": "75%",
  "16:9": "100%",
};

// Mat color behind the fitted audiogram, matched to each template's own
// background so the pillar/letterbox area blends seamlessly instead of
// clashing with a neutral backdrop.
const TEMPLATE_MAT: Record<AudiogramTemplate, string> = {
  "dark-minimal": "#111111",
  "light-editorial": "#FAFAF8",
  "bold-brand": "#0A1628",
};

const GeneratingOverlay = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.78)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: 16,
  zIndex: 3,
  color: "white",
}));

// Dark scrim + blur so the Edit action stays legible even when the audiogram
// preview underneath is dense with text. The scrim fades via its own
// background color (not opacity) so the button layered above stays fully
// opaque, never see-through.
const HoverOverlay = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
  transition: "background-color 0.15s ease, backdrop-filter 0.15s ease",
}));

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export default function AudiogramCard({
  audiogram,
  episodeId,
  compact = false,
}: Props) {
  const isGenerating = audiogram.status === "generating";
  const editorState = audiogramToEditorState(audiogram);
  const matColor = TEMPLATE_MAT[audiogram.template] ?? "#111111";
  const isPortrait = audiogram.aspect_ratio === "9:16";

  const renderWidth = compact
    ? (COMPACT_RENDER_WIDTHS[audiogram.aspect_ratio] ?? "75%")
    : (RENDER_WIDTHS[audiogram.aspect_ratio] ?? "100%");

  const cardContent = (
    <Card
      className={isGenerating ? "generating" : undefined}
      elevation={0}
      sx={compact ? { borderRadius: "10px" } : undefined}
    >
      <PreviewContainer
        sx={{
          backgroundColor: matColor,
          ...(compact ? { aspectRatio: COMPACT_PREVIEW_RATIO } : {}),
        }}
      >
        {isGenerating ? (
          <GeneratingOverlay>
            <CircularProgress size={28} sx={{ color: "#fff" }} />
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, textAlign: "center" }}
              >
                Generating audiogram…
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  opacity: 0.75,
                  mt: 0.5,
                }}
              >
                This may take a few minutes
              </Typography>
            </Box>
          </GeneratingOverlay>
        ) : (
          <>
            <Box
              sx={{
                // Own stacking context (position + zIndex) so the template's
                // internal z-indexes (quote/waveform sit at z-index 3) stay
                // contained below the hover scrim/button instead of poking
                // through them.
                position: "relative",
                zIndex: 0,
                width: renderWidth,
                borderRadius: isPortrait ? "10px" : 0,
                overflow: "hidden",
                boxShadow: isPortrait ? "0 6px 20px rgba(0,0,0,0.28)" : "none",
              }}
            >
              <AudiogramRenderer state={editorState} scale={0.55} />
            </Box>

            <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
              <HoverOverlay className="audiogram-card__scrim">
                <Box
                  className="audiogram-card__action"
                  sx={{
                    visibility: "hidden",
                    backgroundColor: "#2563eb",
                    color: "white",
                    borderRadius: compact ? "8px" : "10px",
                    border: "1px solid #1d4ed8",
                    px: compact ? 1.25 : 2.5,
                    py: compact ? 0.6 : 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: compact ? 0.5 : 1,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
                  }}
                >
                  <EditIcon sx={{ fontSize: compact ? 14 : 20 }} />
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    fontSize={compact ? "0.7rem" : "1rem"}
                  >
                    {compact ? "Edit" : "Edit audiogram"}
                  </Typography>
                </Box>
              </HoverOverlay>
            </RoleBasedAccess>
          </>
        )}
      </PreviewContainer>

      <Box
        sx={{
          p: compact ? 1 : 2,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: compact ? "0.74rem" : "0.9rem",
            color: isGenerating ? "text.disabled" : "text.primary",
            lineHeight: compact ? 1.25 : 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: compact ? 0.5 : 1,
          }}
        >
          {audiogram.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mt: "auto",
          }}
        >
          <Chip
            label={audiogram.aspect_ratio}
            size="small"
            sx={{
              fontSize: compact ? "0.58rem" : "0.7rem",
              height: compact ? 18 : 22,
              backgroundColor: "#f3f0ff",
              color: "#7C6EF5",
              border: "1px solid #d4ccff",
              fontWeight: 600,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: compact ? "0.64rem" : "0.72rem",
              whiteSpace: "nowrap",
            }}
          >
            {formatDate(audiogram.created_at)}
          </Typography>
        </Box>
      </Box>
    </Card>
  );

  return (
    <Grid
      item
      {...(compact ? { xs: 4 } : { xs: 12, sm: 6, md: 6, lg: 4, xl: 3 })}
    >
      {isGenerating ? (
        cardContent
      ) : (
        <Link
          to={`/episodes/${episodeId}/audiograms/${audiogram.id}`}
          style={{ textDecoration: "none", display: "block", height: "100%" }}
        >
          {cardContent}
        </Link>
      )}
    </Grid>
  );
}
